import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n";
import { api, errMsg, fmtTime, fmtMin, todayStr } from "@/lib/api";
import { Card, Loader, PageTitle, GeoBadge, StatusBadge, PositionBadge, Bar } from "@/components/kv";
import { Kpi } from "../staff/StaffHistory";

const LiveList = ({ rows }) => {
  const { t } = useT();
  const withRec = rows.filter((r) => r.last_record);
  return (
    <Card className="p-5" data-testid="live-status-card">
      <h2 className="font-display text-2xl font-bold mb-4">{t("dash.live")}</h2>
      {withRec.length === 0 && <p className="text-sm text-stone-500">{t("dash.nobody")}</p>}
      <div className="divide-y divide-[#E8DFD0]">
        {withRec.map((r) => (
          <div key={r.user.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid={`live-row-${r.user.username}`}>
            <div className="flex items-center gap-3 sm:w-56">
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${r.in_shop ? "bg-green-600 kv-breathe" : "bg-stone-400"}`} />
              <div>
                <div className="font-semibold">{r.user.name}</div>
                <PositionBadge position={r.user.position} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm flex-1">
              <span><span className="text-stone-500">{t("dash.col_in")}:</span> <b className="font-mono-kv">{fmtTime(r.first_in)}</b></span>
              <span><span className="text-stone-500">{t("dash.col_out")}:</span> <b className="font-mono-kv">{fmtTime(r.last_record.clock_out)}</b></span>
              <GeoBadge verified={r.last_record.in_verified} distance={r.last_record.in_distance_m} testid={`live-geo-${r.user.username}`} />
              <span className={`text-xs font-semibold ${r.in_shop ? "text-green-700" : "text-stone-500"}`}>{r.in_shop ? t("dash.inside") : t("dash.left")}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const DailySummary = ({ rows }) => {
  const { t } = useT();
  return (
    <Card className="p-5" data-testid="daily-summary-card">
      <h2 className="font-display text-2xl font-bold mb-4">{t("dash.daily")}</h2>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.user.id} className="flex items-center justify-between gap-2 rounded-xl bg-[#FAF6F0] px-3 py-2.5" data-testid={`daily-row-${r.user.username}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate">{r.user.name}</div>
              <div className="text-xs text-stone-500 font-mono-kv">{r.entry.type === "shift" ? `${r.entry.start}–${r.entry.end}` : "—"}</div>
            </div>
            <div className="text-right space-y-0.5">
              <StatusBadge status={r.status} testid={`daily-status-${r.user.username}`} />
              {r.status === "late" && <div className="text-[11px] text-amber-800">{t("dash.late_by", { m: fmtMin(r.late_min, t) })}</div>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const TaskRates = ({ rows, positions }) => {
  const { t } = useT();
  const Line = ({ label, sub, m, e, id }) => (
    <div className="grid grid-cols-[1fr] sm:grid-cols-[180px_1fr_1fr] gap-2 sm:gap-4 items-center py-2" data-testid={id}>
      <div><div className="font-semibold text-sm">{label}</div>{sub}</div>
      <div><div className="text-[11px] text-stone-500 mb-0.5">{t("periods.morning_s")} · {m.done}/{m.total}</div><Bar done={m.done} total={m.total} /></div>
      <div><div className="text-[11px] text-stone-500 mb-0.5">{t("periods.evening_s")} · {e.done}/{e.total}</div><Bar done={e.done} total={e.total} color="bg-[#46634c]" /></div>
    </div>
  );
  return (
    <Card className="p-5" data-testid="task-rates-card">
      <h2 className="font-display text-2xl font-bold mb-2">{t("dash.tasks")}</h2>
      <div className="font-mono-kv text-[11px] uppercase tracking-widest text-[#99432b] mt-3">{t("dash.by_staff")}</div>
      <div className="divide-y divide-[#E8DFD0]">
        {rows.map((r) => <Line key={r.user.id} id={`task-rate-${r.user.username}`} label={r.user.name} sub={<PositionBadge position={r.user.position} />} m={r.tasks.morning} e={r.tasks.evening} />)}
      </div>
      <div className="font-mono-kv text-[11px] uppercase tracking-widest text-[#99432b] mt-5">{t("dash.by_position")}</div>
      <div className="divide-y divide-[#E8DFD0]">
        {positions.map((p) => <Line key={p.position} id={`task-rate-pos-${p.position}`} label={t(`positions.${p.position}`)} m={p.morning} e={p.evening} />)}
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const { t } = useT();
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/overview", { params: { date } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, t)));
  }, [date, t]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const rows = data?.rows || [];
  return (
    <div>
      <PageTitle kicker={t("dash.auto")} title={t("dash.title")}>
        <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-44 bg-white rounded-full" data-testid="dashboard-date-input" />
        <Button variant="outline" className="rounded-full" onClick={load} data-testid="dashboard-refresh-button"><RefreshCw className="h-4 w-4 mr-1.5" />{t("common.refresh")}</Button>
      </PageTitle>
      {!data ? <Loader /> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label={t("dash.in_shop")} value={rows.filter((r) => r.in_shop).length} accent="text-green-700" testid="kpi-in-shop" />
            <Kpi label={t("dash.scheduled")} value={rows.filter((r) => r.entry.type === "shift" || r.status === "extra").length} testid="kpi-scheduled" />
            <Kpi label={t("dash.on_leave")} value={rows.filter((r) => ["leave", "off"].includes(r.status)).length} accent="text-violet-700" testid="kpi-on-leave" />
            <Kpi label={t("dash.late")} value={rows.filter((r) => ["late", "not_arrived"].includes(r.status)).length} accent="text-red-700" testid="kpi-late" />
          </div>
          <div className="grid xl:grid-cols-[1.4fr_1fr] gap-5">
            <LiveList rows={rows} />
            <DailySummary rows={rows} />
          </div>
          <TaskRates rows={rows} positions={data.positions} />
          {rows.some((r) => r.overtime_min > 0) && (
            <Card className="p-5" data-testid="today-overtime-card">
              <h2 className="font-display text-2xl font-bold mb-3">{t("report.overtime")} · {t("common.today")}</h2>
              <div className="flex flex-wrap gap-2">
                {rows.filter((r) => r.overtime_min > 0).map((r) => (
                  <span key={r.user.id} className="rounded-full bg-orange-100 text-orange-800 px-3 py-1 text-sm font-semibold">{r.user.name}: +{fmtMin(r.overtime_min, t)}</span>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
